const accumulatedWeight = (tests) => tests.reduce((acc, test) => acc + (test.weight || 0), 0);
const LAZY_PATTERN = /^([A-Z]+)?([a-z]*)(\d*)([^\w\d]*)$/;

export const shortenThan = (password, context) =>
    password.length < context.minLength && {
        error: 'too-short',
        message: 'The password should not be shorter than {{length}} characters.',
        length: context.minLength,
    };
export const longerThan = (password, context) =>
    password.length > context.maxLength && {
        error: 'too-long',
        message: 'The password should not be longer than {{length}} characters.',
        length: context.maxLength,
    };
export const repetitive = (password, context) =>
    password.match(/(.)\1{2,}/) && {
        error: 'too-repetitive',
        message: 'The password should not contain sequences of same characters.',
    };
export const noLowercase = (password, context) =>
    !password.match(/[a-z]/) && {
        error: 'no-lowercase',
        message: 'The password should contain at least one lowercase character.',
    };
export const noUppercase = (password, context) =>
    !password.match(/[A-Z]/) && {
        error: 'no-uppercase',
        message: 'The password should contain at least one uppercase character.',
    };
export const noNumbers = (password, context) =>
    !password.match(/\d/) && {
        error: 'no-numbers',
        message: 'The password should contain any number.',
    };
export const noSymbols = (password, context) =>
    !password.match(/[A-Za-z\d]/) && {
        error: 'no-symbols',
        message: 'The password should contain at least one special character.',
    };

export const lazyPassword = (password, context) => {
    if (!(password.length && password.length < context.arguablySafeLength)) {
        return;
    }

    const SYMBOLS = ['', 'uppercase', 'lowercase', 'numbers', 'symbols'];
    const match = password.match(LAZY_PATTERN);

    if (!match) {
        return;
    }

    const parts = SYMBOLS.map((symbol, index) => symbol && match[index]);
    const pattern = parts
        .map((part, index) => part && SYMBOLS[index])
        .filter(Boolean)
        .join('+');

    return {
        error: 'too-easy',
        message:
            'The password should not conform to easily guessable pattern "{{pattern}}", or it should be long enough ({{length}}+ symbols).',
        pattern,
        length: context.arguablySafeLength,
    };
};

export class PasswordValidator {
    required = [];
    optional = [];

    constructor(config = {}) {
        Object.assign(this, config);
    }

    validate(password) {
        const errors = [];

        for (const [index, tests] of [this.required, this.optional].entries()) {
            for (const { fn, ...rest } of tests) {
                const result = fn(password, this);

                if (result) {
                    errors.push(Object.assign(result, { required: !index, ...rest }));
                }
            }
        }

        return errors;
    }

    estimate(errors) {
        const total = accumulatedWeight([...this.required, ...this.optional]);
        const exclusive = errors.find((e) => e.required);
        const weight = exclusive ? 0 : total - accumulatedWeight(errors);

        return {
            total,
            weight,
        };
    }
}

export const passwordValidator = (config) =>
    new PasswordValidator({
        minLength: 10,
        maxLength: 100,
        arguablySafeLength: 20,
        required: [{ weight: 50, fn: shortenThan }, { weight: 0, fn: longerThan }],
        optional: [
            { weight: 25, fn: lazyPassword },
            { weight: 5, fn: repetitive },
            { weight: 5, fn: noLowercase },
            { weight: 5, fn: noUppercase },
            { weight: 5, fn: noNumbers },
            { weight: 5, fn: noSymbols },
        ],
        ...config,
    });
